import { useCallback, useEffect, useMemo, useState, CSSProperties } from "react";
import { useLocation, Link } from "react-router-dom";
import { useUser } from "@/stores/userStore";
import { useOrganizationData } from "@/stores/commonStore";
import { cn } from "@/lib/utils";
import {
  Home,
  Gift,
  History,
  Info,
  User,
  LogOut,
  Menu,
  UserCog2,
  X,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/config";
import NavBar from "../common/NavBar";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAuthSync } from "@/stores/useAuthSync";

const MobileMenuSepartor = (
  { className = "" }: { className?: string }
) => <div className={
  cn(
    "w-full h-[1px] bg-gradient-to-r from-secondary/80 via-accent to-primary/80",
    className
  )
} />

export default function Sidebar() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { signoutRedirect: logout } = useAuthSync();
  const { isLoading, user } = useUser();
  const { organizationName, configuration: { portalConfiguration: { showRewards } } } = useOrganizationData();

  const navItems = useMemo(() => {
    const items = [
      { icon: Home, href: "/auth/home", label: "Home" },
      { icon: Gift, href: "/auth/rewards", label: "Rewards" },
      { icon: History, href: "/auth/transactions", label: "Transactions" },
      { icon: Info, href: "/auth/about", label: "About" },
    ];

    if (!showRewards) return items.filter(i => i.href !== "/auth/rewards")

    return items;

  }, [showRewards]);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await logout({ post_logout_redirect_uri: window.location.origin });
    },
    onError: (err: any) => {
      console.error("Failed to logout:", err);
      toast.error(
        <div>
          {err?.message ? <div>
            <div className="text-lg">Failed to logout!</div>
            <div className="secondary-font">Error: {err.message}</div>
          </div> : "Oops! Something went wrong. Please try again."}
          <small>If the issue persists, please contact {organizationName} support.</small>
        </div>
      );
    },
  });

  const handleLogout = useCallback(() => {
    if (!logoutMutation.isPending) {
      logoutMutation.mutate();
    }
  }, [logoutMutation]);

  useEffect(() => {
    if (location.pathname) {
      setIsMobileMenuOpen(false)
    }
  }, [location.pathname])

  return (
    <>
      {/* Mobile Navigation */}
      <div className="md:hidden">
        <NavBar
          className="sm:h-16 fixed bg-transparent bg-gradient-to-r from-secondary to-primary/80"
          drawerOpen={isMobileMenuOpen}
          setDrawerOpen={setIsMobileMenuOpen}
          children={
            <>
              <div className="h-full flex items-center justify-between px-4 py-2">
                <div className="w-16 h-16 p-1">
                  <Link to="/auth/home">
                    <img
                      className="hover:scale-105 transition-transform duration-300 w-full h-full object-contain"
                      src={ASSETS.CUSTOMER_LOGO}
                      alt={organizationName}
                    />
                  </Link>
                </div>
                {("/auth/rewards" === location.pathname) &&
                  <div className='text-xs text-secondary-color/70 py-1 w-[12rem] flex flex-col justify-center items-center rounded-lg bg-primary/30 shadow-lg'>
                    Redeemable Points
                    <div className="font-semibold text-sm text-secondary-color secondary-font">
                      {isLoading ?
                        <span className="text-xs animate-pulse">Loading...</span>
                        :
                        user?.points?.toLocaleString("en-US", { minimumFractionDigits: 2 }) ?? "~unknown"
                      }
                    </div>
                  </div>
                }
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="text-secondary-color hover:text-accent"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>
              <MobileMenuSepartor className={!isMobileMenuOpen ? "h-[3px]" : ""} />
            </>
          }
          mobileChildren={
            <div className="h-full w-full flex flex-col justify-between items-center">
              <div className="w-full flex justify-end items-center p-3">
                <button
                  className="text-2xl text-secondary hover:text-destructive"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="w-full h-full flex flex-col justify-between items-center">
                <div className="w-full">
                  {navItems.map((item: any) => {
                    const Icon = item.icon;
                    const isActive =
                      (["", "/"].includes(location.pathname) && item.href === "/auth/home") ||
                      location.pathname === item.href;

                    return (
                      <Link
                        key={item.href}
                        className={cn(
                          "flex items-center space-x-2 px-4 py-3",
                          isActive
                            ? "sidebar-active-item"
                            : "text-secondary-color hover:bg-accent/30 sidebar-item"
                        )}
                        style={isActive ? {
                          backgroundColor: 'var(--sidebar-active-bg)',
                          color: 'var(--sidebar-active-fg)'
                        } as CSSProperties : {}}
                        to={item.href}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  <Link
                    className={cn(
                      "flex items-center space-x-2 px-4 py-3",
                      location.pathname.startsWith("/auth/profile")
                        ? "text-primary-color bg-accent/75"
                        : "text-secondary-color hover:bg-accent/30 sidebar-item"
                    )}
                    to="/auth/profile"
                  >
                    <User className="w-5 h-5" />
                    <span>My Profile</span>
                  </Link>
                </div>
                <div className="w-full flex justify-center items-center">
                  <Button
                    className="w-full max-w-[200px] flex justify-center items-center space-x-2 my-2 px-4 py-3"
                    variant="destructive"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="">Sign Out</span>
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      </div>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col justify-between fixed left-0 top-0 h-full w-20 bg-transparent bg-gradient-to-b from-secondary to-primary/80 border-r border-primary/5" style={{ background: 'var(--sidebar-bg)' } as CSSProperties}>
        <div className="flex flex-col items-center pb-6">
          <div className="w-16 h-16 p-1 sidebar-logo-container">
            <Link to="/auth/home">
              <img
                className="hover:scale-105 transition-transform duration-300 w-full h-full object-contain"
                src={ASSETS.CUSTOMER_LOGO}
                alt={organizationName}
              />
            </Link>
          </div>
          <nav className="flex flex-col space-y-6 mt-4">
            {navItems.map((item: any) => {
              const Icon = item.icon;
              const isActive =
                (["", "/"].includes(location.pathname) && item.href === "/auth/home") ||
                location.pathname === item.href;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "p-3 rounded-lg transition-colors",
                    isActive
                      ? ""
                      : "hover:bg-accent/30"
                  )}
                  style={isActive ? {
                    backgroundColor: 'var(--sidebar-active-bg)',
                    color: 'var(--sidebar-active-fg)'
                  } as CSSProperties : {}}
                  title={item.label}
                >
                  <Icon className="h-6 w-6" />
                </Link>
              );
            })}
          </nav>
        </div>
        {/* User Profile Dropdown */}
        <div className="flex flex-col items-center pb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isLoading}>
              <div className={`p-3 rounded-lg text-secondary-color ${!isLoading ? "hover:bg-accent/30" : ""}`}>
                {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : <UserCog2 className="h-6 w-6" />}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 sidebar-user-menu"
            >
              {"/auth/home" !== location.pathname &&
                <>
                  <div className="w-full flex justify-between my-2">
                    <div className='text-sm text-secondary-color/70 mx-3 w-full p-3 flex flex-col justify-center items-center rounded-lg bg-primary/30 shadow-lg'>
                      Redeemable Points
                      <div className="font-semibold text-lg text-secondary-color secondary-font">
                        {isLoading ?
                          <span className="text-xs animate-pulse">Loading...</span>
                          :
                          user?.points?.toLocaleString("en-US", { minimumFractionDigits: 2 }) ?? "~unknown"
                        }
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator className="bg-accent" />
                </>
              }
              <DropdownMenuItem
                onClick={() => {
                  window.location.href = "/portal/auth/profile"
                }}
                className={`${location.pathname === "/auth/profile"
                  ? "bg-accent"
                  : "text-secondary-color hover:bg-accent/30"
                  }`}
              >
                <User className="w-4 h-4 mr-2" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-accent" />
              <DropdownMenuItem
                className="hover:bg-transparent focus:bg-transparent"
              >
                <Button
                  className="w-full flex justify-center items-center"
                  variant="destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5" />
                  <span className="">Sign Out</span>
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </>
  );
}
